import React from "react";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { IParticipationSummary } from "@Interfaces";
import {
    getParticipationDataPhase,
    participationCardSx,
    participationIconChipSx,
    participationValueSx,
    PARTICIPATION_ICON_SIZE,
    PARTICIPATION_ICON_STROKE,
    PARTICIPATION_MUTED_TEXT,
    PARTICIPATION_TEXT_LG,
    PARTICIPATION_TEXT_SM,
} from "./participationStyles";

interface EngagementStatTilesProps {
    summary: IParticipationSummary;
}

export const EngagementStatTiles: React.FC<EngagementStatTilesProps> = ({
    summary,
}) => {
    const { t } = useTranslation();
    const phase = getParticipationDataPhase(summary.respondents);
    const showAnsweredAll = summary.questions_count > 1;

    const answeredAllValue =
        phase === "none"
            ? "–"
            : phase === "few"
            ? String(summary.respondents_answered_all)
            : `${Math.round(
                  (summary.respondents_answered_all / summary.respondents) * 100
              )}%`;

    const answeredAllSub =
        phase === "none"
            ? t("pages.participation.widgets.answeredEveryQuestion.none")
            : phase === "few"
            ? t("pages.participation.widgets.answeredEveryQuestion.few", {
                  respondents: summary.respondents,
              })
            : t("pages.participation.widgets.answeredEveryQuestion.many", {
                  answered: summary.respondents_answered_all,
                  respondents: summary.respondents,
              });

    const wroteSomethingValue =
        phase === "none" ? "–" : String(summary.respondents_wrote_something);
    const wroteSomethingSub =
        phase === "none"
            ? t("pages.participation.widgets.wroteSomething.none")
            : t("pages.participation.widgets.wroteSomething.sub");

    return (
        <>
            {showAnsweredAll && (
                <Box sx={participationCardSx}>
                    <Box sx={participationIconChipSx}>
                        <svg
                            width={PARTICIPATION_ICON_SIZE}
                            height={PARTICIPATION_ICON_SIZE}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke={PARTICIPATION_ICON_STROKE}
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M4 12.5l4.5 4.5L20 5.5" />
                            <path d="M4 7h6" />
                            <path d="M4 20h16" />
                        </svg>
                    </Box>
                    <Box sx={participationValueSx(phase === "none")}>
                        {answeredAllValue}
                    </Box>
                    <Box
                        sx={{
                            fontSize: PARTICIPATION_TEXT_LG,
                            color: PARTICIPATION_MUTED_TEXT,
                        }}
                    >
                        {t(
                            "pages.participation.widgets.answeredEveryQuestion.label"
                        )}
                    </Box>
                    <Box
                        sx={{
                            fontSize: PARTICIPATION_TEXT_SM,
                            color: PARTICIPATION_MUTED_TEXT,
                        }}
                    >
                        {answeredAllSub}
                    </Box>
                </Box>
            )}

            <Box sx={participationCardSx}>
                <Box sx={participationIconChipSx}>
                    <svg
                        width={PARTICIPATION_ICON_SIZE}
                        height={PARTICIPATION_ICON_SIZE}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={PARTICIPATION_ICON_STROKE}
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M4 5h16v11H9l-5 4z" />
                    </svg>
                </Box>
                <Box sx={participationValueSx(phase === "none")}>
                    {wroteSomethingValue}
                </Box>
                <Box
                    sx={{
                        fontSize: PARTICIPATION_TEXT_LG,
                        color: PARTICIPATION_MUTED_TEXT,
                    }}
                >
                    {t("pages.participation.widgets.wroteSomething.label")}
                </Box>
                <Box
                    sx={{
                        fontSize: PARTICIPATION_TEXT_SM,
                        color: PARTICIPATION_MUTED_TEXT,
                    }}
                >
                    {wroteSomethingSub}
                </Box>
            </Box>
        </>
    );
};
